/**
 * EstateFactsSection — the estate's structural facts as crawlable SSR text
 * (SEO F1).
 *
 * Every value here is DB-resident and was never rendered before: the estate
 * page said the estate's name, showed a map, and stopped.
 *
 * These rows are a SUBSET of what the backend's estate `FAQPage` answers, not
 * its answer set — a claim this header used to make, and B.3b's root cause.
 * The FAQ also answers the nearest MTR station and the nearest school, which
 * no row here carries, so "the structured data says only what the grid shows"
 * was false. The Q&A the JSON-LD describes is rendered by
 * `<EstateFaqSection>`, from the very items the JSON-LD is built from.
 *
 * Absence is never rendered as a zero — the backend already folded the DB's
 * in-band "unknown" encodings to absent, and a renderer that returns
 * `undefined` drops its row entirely.
 */
import { useTranslations, type TranslateFunction } from '@/shared/i18n/useTranslations';
import type { EstateFactsData } from '@/shared/lib/types/estates';
import { projectLocalized } from '@letsgethome/lib/i18n';
import {
  plaintextString,
  type Locale,
  type LocalizedText,
  type PlaintextString,
} from '@letsgethome/types/branded';

type Props = {
  facts: EstateFactsData;
  locale: Locale;
};

type FactRow = {
  /** Stable React key + the row's semantic name. */
  key: string;
  /** Always a translated label. */
  label: LocalizedText;
  /** Either a translated word (Yes/No) or verbatim data (a count, a name). */
  value: LocalizedText | PlaintextString;
};

/** What a renderer needs beyond the fact value itself. */
type RenderContext = {
  t: TranslateFunction<'estates.detail'>;
  locale: Locale;
};

/**
 * ONE renderer per fact, keyed by the fact's own field name.
 *
 * The map is typed `Record<keyof Required<EstateFactsData>, …>`: adding a
 * field to `EstateFactsData` (the next tranche of estate enrichment will)
 * stops this file compiling until the new fact is given a rendering — or an
 * explicit `() => undefined` skip. The grid therefore cannot silently drop a
 * fact the backend has started sending, which is the failure mode that left
 * these columns invisible for years in the first place.
 */
const FACT_RENDERERS: {
  [K in keyof Required<EstateFactsData>]: (
    facts: EstateFactsData,
    ctx: RenderContext
  ) => FactRow | undefined;
} = {
  phase: (facts, { t, locale }) =>
    facts.phase === undefined
      ? undefined
      : {
          key: 'phase',
          label: t('facts.phase'),
          value: plaintextString(projectLocalized(facts.phase.name, locale)),
        },
  totalBuildingsCount: (facts, { t }) =>
    facts.totalBuildingsCount === undefined
      ? undefined
      : {
          key: 'blocks',
          label: t('facts.blocks'),
          value: plaintextString(String(facts.totalBuildingsCount)),
        },
  lift: (facts, { t }) =>
    // Presence is meaning (wire epoch 30): `undefined` = never classified →
    // no row. Both DEFINED states DO render — for HK stock the meaningful
    // signal is a measured walk-up, and the column is a trusted search filter
    // in both directions. The `=== 'has-lift'` compare (vs truthiness) is why
    // the union can't collapse absence and a walk-up.
    facts.lift === undefined
      ? undefined
      : {
          key: 'lift',
          label: t('facts.lift'),
          value: facts.lift === 'has-lift' ? t('facts.yes') : t('facts.no'),
        },
};

/**
 * Display order — independent of the type's field order. No "Type: Village
 * house" row (#8803 step 3, D-388): it restated the estate's hand-kept village
 * flag; the page's derived "Houses (N)" already says when a tree is houses.
 */
const FACT_ORDER = ['phase', 'totalBuildingsCount', 'lift'] as const satisfies ReadonlyArray<
  keyof Required<EstateFactsData>
>;

export function EstateFactsSection({ facts, locale }: Props) {
  const t = useTranslations('estates.detail');
  const rows = FACT_ORDER.map((key) => FACT_RENDERERS[key](facts, { t, locale })).filter(
    (row): row is FactRow => row !== undefined
  );

  if (rows.length === 0) {
    return null;
  }

  return (
    <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <h2 className="mb-6 text-2xl font-bold">{t('facts.title')}</h2>
      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {rows.map((row) => (
          <div key={row.key} className="rounded-xl bg-muted p-4">
            <dt className="text-sm text-muted-foreground">{row.label}</dt>
            <dd className="mt-1 font-medium text-foreground">{row.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
